import { Editable, Slate, withReact } from "slate-react";
import { MentionStore, Suggestions } from "./MentionStore";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import { withSimpleEditorPlugins } from "./utils/withSimpleEditorPlugins";
import { Element } from "./components/Element";
import { MentionMenu } from "./components/Mention";
import { simpleDeserialize } from "./utils/simpleDeserialize";
import { simpleSerialize } from "./utils/simpleSerialize";

interface SimpleEditorProps {
  initialValue?: string;
  suggestions?: Suggestions;
  disabled?: boolean;
  withFontFamily?: boolean;
}

export interface SimpleEditorInstance {
  getText: () => string;
  setText: (text: string) => void;
}

export const SimpleEditor = forwardRef<SimpleEditorInstance, SimpleEditorProps>((props, ref) => {
  const editor = useMemo(() => withSimpleEditorPlugins(withHistory(withReact(createEditor()))), []);
  const mentionStore = useMemo(() => new MentionStore(editor, props.suggestions), [editor]);
  const renderElement = useCallback((props) => <Element {...props} />, []);

  // Initialize value from initialValue or default to empty paragraph
  const [value, setValue] = useState(() =>
    props.initialValue
      ? simpleDeserialize(props.initialValue, mentionStore.getSuggestionLabel)
      : [{ type: "paragraph" as const, children: [{ text: "" }] }]
  );

  // Update suggestions when props.suggestions changes
  useEffect(() => {
    if (props.suggestions) {
      mentionStore.setSuggestions(props.suggestions);
    }
  }, [props.suggestions, mentionStore]);

  // Update value when initialValue changes
  useEffect(() => {
    if (props.initialValue) {
      const newValue = simpleDeserialize(props.initialValue, mentionStore.getSuggestionLabel);
      setValue(newValue);
      editor.children = newValue;
    }
  }, [props.initialValue, editor]);

  const handleChange = (values) => {
    mentionStore.onEditorChange();
  };

  useImperativeHandle(
    ref,
    () => ({
      getText: () => {
        return simpleSerialize(editor.children);
      },
      setText: (text: string) => {
        if (!text) {
          const newNode = { type: "paragraph" as const, children: [{ text: " " }] };
          setValue([newNode]);
          editor.children = [newNode];
        } else {
          const newValue = simpleDeserialize(text, mentionStore.getSuggestionLabel);
          setValue(newValue);
          editor.children = newValue;
        }
      },
    }),
    []
  );

  return (
    <div className="slate-editor">
      <Slate editor={editor} initialValue={value} onValueChange={handleChange}>
        <Editable
          readOnly={props.disabled}
          className="slate--editable"
          renderElement={renderElement}
          spellCheck
          onKeyDown={mentionStore.onKeyDown}
        />

        <MentionMenu mentionStore={mentionStore} editor={editor} />
      </Slate>
    </div>
  );
});
